import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";

// ── Constants ─────────────────────────────────────────────────────────────────

const STORAGE_KEY    = "integro_cookie_consent";
const CONSENT_VERSION = "1";

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Read the stored consent object, or null if none / version mismatch. */
function getStoredConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed.version === CONSENT_VERSION ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Persist the consent choice and push a Consent Mode v2 update to GA4.
 * This is called both from the React component (on user action) and is
 * mirrored by the synchronous restore in public/index.html for page loads
 * where a preference already exists.
 */
function applyConsent(analytics) {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ analytics, version: CONSENT_VERSION, timestamp: Date.now() })
  );
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage:  analytics ? "granted" : "denied",
      ad_storage:         "denied",
      ad_user_data:       "denied",
      ad_personalization: "denied",
    });
  }
}

// ── Sub-components ────────────────────────────────────────────────────────────

/** Accessible toggle switch. */
const Toggle = ({ checked, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={onChange}
    className={[
      "relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center",
      "rounded-full border-2 border-transparent transition-colors duration-200",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#005eb8] focus-visible:ring-offset-2",
      checked ? "bg-[#4caf50]" : "bg-gray-300",
    ].join(" ")}
  >
    <span
      className={[
        "pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm",
        "transition-transform duration-200",
        checked ? "translate-x-5" : "translate-x-0",
      ].join(" ")}
    />
  </button>
);

/** Consent category row used in the preferences panel. */
const CategoryRow = ({ title, description, alwaysOn, checked, onChange }) => (
  <div className="p-3 bg-[#f8fafc] rounded-xl border border-gray-100">
    <div className="flex items-center justify-between mb-1.5">
      <span className="text-sm font-semibold text-[#012d5c]">{title}</span>
      {alwaysOn ? (
        <span className="text-xs bg-[#e0f2fe] text-[#005eb8] px-2 py-0.5 rounded-full font-medium">
          Always active
        </span>
      ) : (
        <Toggle
          checked={checked}
          onChange={onChange}
          label={`Toggle ${title}`}
        />
      )}
    </div>
    <p className="text-xs text-gray-500 leading-relaxed">{description}</p>
  </div>
);

// ── Main component ─────────────────────────────────────────────────────────────

/**
 * CookieConsent
 *
 * Renders a fixed consent banner when no preference is stored.
 * Exposes a preferences panel via the "Manage" button and via a custom
 * window event ("openCookieSettings") dispatched by the footer link.
 *
 * Two views:
 *   1. Simple  — heading + description + Accept / Reject / Manage buttons
 *   2. Details — category toggles + Accept all / Save preferences buttons
 *
 * On mobile  : full-width bar anchored to the bottom of the viewport.
 * On desktop : compact card, bottom-right corner.
 */
const CookieConsent = () => {
  const [visible,     setVisible]     = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics,   setAnalytics]   = useState(false);

  // ── Mount: decide whether to show banner ────────────────────────────────
  useEffect(() => {
    if (!getStoredConsent()) {
      setVisible(true);
    }
  }, []);

  // ── Listen for footer "Cookie Settings" link ────────────────────────────
  const handleReopen = useCallback(() => {
    const saved = getStoredConsent();
    setAnalytics(saved ? Boolean(saved.analytics) : false);
    setShowDetails(true);
    setVisible(true);
  }, []);

  useEffect(() => {
    window.addEventListener("openCookieSettings", handleReopen);
    return () => window.removeEventListener("openCookieSettings", handleReopen);
  }, [handleReopen]);

  // ── Actions ─────────────────────────────────────────────────────────────
  const acceptAll = () => {
    applyConsent(true);
    setVisible(false);
    setShowDetails(false);
  };

  const rejectAll = () => {
    applyConsent(false);
    setVisible(false);
    setShowDetails(false);
  };

  const savePreferences = () => {
    applyConsent(analytics);
    setVisible(false);
    setShowDetails(false);
  };

  const openDetails = () => {
    const saved = getStoredConsent();
    setAnalytics(saved ? Boolean(saved.analytics) : false);
    setShowDetails(true);
  };

  // ── Don't render when not needed ────────────────────────────────────────
  if (!visible) return null;

  // ── Shared button styles ─────────────────────────────────────────────────
  const btnPrimary =
    "flex-1 bg-[#4caf50] hover:bg-[#3d8b40] text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors";
  const btnNavy =
    "flex-1 bg-[#012d5c] hover:bg-[#005eb8] text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors";
  const btnNeutral =
    "flex-1 bg-gray-100 hover:bg-gray-200 text-[#012d5c] text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors";
  const btnOutline =
    "flex-1 border border-[#005eb8]/40 hover:border-[#005eb8] text-[#005eb8] text-sm font-medium px-4 py-2.5 rounded-lg transition-colors";

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie preferences"
      aria-live="polite"
      className={[
        // Base — mobile: full-width bottom bar
        "fixed bottom-0 left-0 right-0 z-[9999]",
        "bg-white shadow-[0_-4px_28px_rgba(1,45,92,0.12)]",
        "rounded-t-2xl border-t border-gray-200",
        // Desktop: compact card, bottom-right
        "md:bottom-6 md:left-auto md:right-6 md:w-[440px]",
        "md:rounded-2xl md:border md:border-gray-200",
        "md:shadow-[0_8px_40px_rgba(1,45,92,0.18)]",
      ].join(" ")}
    >
      <div className="p-5 md:p-6">

        {/* ── Simple view ────────────────────────────────────────────── */}
        {!showDetails && (
          <>
            <h2 className="text-base font-bold text-[#012d5c] mb-2">
              Your cookie preferences
            </h2>
            <p className="text-sm text-gray-600 leading-relaxed mb-4">
              We use analytics cookies to understand how visitors use our
              website and to improve the experience. Read our{" "}
              <Link
                to="/privacy-policy"
                className="text-[#005eb8] underline underline-offset-2 hover:text-[#4caf50] transition-colors"
                onClick={() => setVisible(false)}
              >
                Privacy Policy
              </Link>{" "}
              for details. You can change your preferences at any time.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button onClick={acceptAll}  className={btnPrimary}>Accept all</button>
              <button onClick={rejectAll}  className={btnNeutral}>Reject all</button>
              <button onClick={openDetails} className={btnOutline}>Manage</button>
            </div>
          </>
        )}

        {/* ── Preferences panel ──────────────────────────────────────── */}
        {showDetails && (
          <>
            {/* Header row */}
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={() => setShowDetails(false)}
                aria-label="Back to cookie notice"
                className="p-1 -ml-1 rounded text-gray-400 hover:text-[#012d5c] transition-colors"
              >
                {/* Left-arrow icon */}
                <svg
                  width="20" height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M19 12H5" />
                  <path d="M12 5l-7 7 7 7" />
                </svg>
              </button>
              <h2 className="text-base font-bold text-[#012d5c]">
                Manage cookie preferences
              </h2>
            </div>

            {/* Categories */}
            <div className="space-y-3 mb-5">
              <CategoryRow
                title="Essential cookies"
                description="Required for the website to function correctly. These include security, routing, and session cookies and cannot be disabled."
                alwaysOn
              />
              <CategoryRow
                title="Analytics cookies"
                description="Google Analytics (GA4) helps us understand how visitors use our site — which pages are popular, where traffic comes from, and how to improve. No personal data is shared for advertising. Analytics cookies are only used when you give consent. You can change your preferences at any time."
                checked={analytics}
                onChange={() => setAnalytics(prev => !prev)}
              />
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2">
              <button onClick={acceptAll}       className={btnPrimary}>Accept all</button>
              <button onClick={savePreferences} className={btnNavy}>Save preferences</button>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default CookieConsent;
